import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { ExternalLink } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyUtils';
import type { ExchangeRates } from '../../utils/currencyUtils';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface CryptoDropdownProps {
  isOpen: boolean;
  market: any;
  currency: string;
  exchangeRates: ExchangeRates;
  priceHistory?: { timestamp: number; price: number }[];
  onClose: () => void;
}

export function CryptoDropdown({ 
  isOpen, 
  market, 
  currency, 
  exchangeRates,
  priceHistory = [],
  onClose 
}: CryptoDropdownProps) {
  // Generate sample data if no price history is available
  const generateSampleData = () => {
    const data = [];
    const now = Date.now();
    const hours = 24;
    const basePrice = market.current_price;
    const volatility = 0.02; // 2% volatility

    for (let i = 0; i < hours; i++) {
      const timestamp = now - (hours - i) * 3600000;
      const randomChange = (Math.random() - 0.5) * volatility;
      const price = basePrice * (1 + randomChange);
      data.push({ timestamp, price });
    }
    return data;
  };

  // Use actual price history or generate sample data
  const chartData = {
    labels: (priceHistory.length > 0 ? priceHistory : generateSampleData()).map(point => 
      new Date(point.timestamp).toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      })
    ),
    datasets: [
      {
        label: 'Price',
        data: (priceHistory.length > 0 ? priceHistory : generateSampleData()).map(point => point.price),
        borderColor: market.price_change_percentage_24h >= 0 ? '#22c55e' : '#ef4444',
        backgroundColor: (context: any) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 200);
          if (market.price_change_percentage_24h >= 0) {
            gradient.addColorStop(0, 'rgba(34, 197, 94, 0.2)');
            gradient.addColorStop(1, 'rgba(34, 197, 94, 0)');
          } else {
            gradient.addColorStop(0, 'rgba(239, 68, 68, 0.2)');
            gradient.addColorStop(1, 'rgba(239, 68, 68, 0)');
          }
          return gradient;
        },
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBackgroundColor: market.price_change_percentage_24h >= 0 ? '#22c55e' : '#ef4444',
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 2
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#1f2937',
        bodyColor: '#1f2937',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        displayColors: false,
        callbacks: {
          title: (tooltipItems: any) => {
            return tooltipItems[0].label;
          },
          label: (context: any) => {
            return formatCurrency(context.raw, currency, exchangeRates);
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false
        },
        ticks: {
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 6,
          color: '#9ca3af'
        }
      },
      y: {
        display: true,
        position: 'right' as const,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false
        },
        ticks: {
          color: '#9ca3af',
          callback: (value: any) => formatCurrency(value, currency, exchangeRates)
        }
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2 }}
          className="col-span-full overflow-hidden"
        >
          <div className="bg-gray-50 p-6 border-t border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Price Chart */}
              <div className="md:col-span-2 bg-white rounded-xl p-6 shadow-sm">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Price Chart (24h)
                  </h3>
                  <p className="text-sm text-gray-500">
                    {market.name} ({market.symbol.toUpperCase()})
                  </p>
                </div>
                <div className="h-64">
                  <Line data={chartData} options={chartOptions} />
                </div>
              </div>

              {/* Market Info */}
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Market Stats</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Market Cap</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(market.market_cap, currency, exchangeRates)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">24h Volume</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(market.total_volume, currency, exchangeRates)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Circulating Supply</span>
                      <span className="text-sm font-medium text-gray-900">
                        {market.circulating_supply.toLocaleString()} {market.symbol.toUpperCase()}
                      </span>
                    </div>
                    {market.max_supply && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Max Supply</span>
                        <span className="text-sm font-medium text-gray-900">
                          {market.max_supply.toLocaleString()} {market.symbol.toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => window.open(`https://www.coingecko.com/en/coins/${market.id}`, '_blank')}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  View Details
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}